import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useRedeploymentPlanStore, { DEFAULT_NOTIFICATIONS } from 'Store/useRedeploymentPlanStore';

/**
 * usePaicRedesignPlanStore
 *
 * Session-only draft of a PAIC Org Redesign plan — PAIC Org Redesign's
 * counterpart to useRedeploymentPlanStore / useWorkforcePlanStore, saved as
 * its own plan type (see usePaicRedesignPlanActions) so it never shows up
 * among, or overwrites, redeployment plans.
 *
 * One plan covers exactly ONE published org redesign version (one project) —
 * the backend rejects more. The version page starts a fresh plan when the
 * user opens a different version (getOtherDraftVersion; it asks first when
 * that other version has unsaved changes or is the saved plan being edited,
 * and drops one that was only browsed). A version
 * joins the draft (ensureVersion) the first time the user edits it or opens
 * one of its roles, seeded with its Roles Reduced / Roles Added roles at their
 * AI-proposed headcount (buildPaicVersionBaseline):
 *
 *   versions: {
 *     "<sb_org_redesign_unique_id>": {
 *       roles: {
 *         "<role_key>": {
 *           role_key,              // the version role's sb_unique_id
 *           jd_sb_unique_id,       // client-side only, never sent
 *           change_type,           // the version's API status: "REDUCED" | "INCREASED"
 *           target_position,       // the "After HC (Approved)" value
 *           available_employee_targets: [...],  // shortlisted employees
 *         },
 *       },
 *     },
 *   }
 *
 * Employees are picked on the shared Reduction Detail page, which only knows
 * useRedeploymentPlanStore. PaicReductionDetailV2 seeds that store with one
 * role's draft and links the role here (linkRole); the subscription at the
 * bottom of this file copies every later change of that role back. The
 * redeployment store is only ever read here — never changed by this module.
 *
 * Change tracking: pristineVersions holds each version as it was when it
 * joined the draft (its baseline) or was last saved. A version is "changed"
 * when its roles differ from that reference (compared in the saved shape,
 * so client-only bookkeeping never counts as a change); the Save buttons are
 * only enabled while some version is changed (useHasPaicRedesignPlanChanges).
 * A save sends the changed versions plus every version already in the saved
 * plan (savedVersionIds), each with only the roles employees were shortlisted
 * for — a version that was only browsed (e.g. a role opened and closed
 * without picks) is never added to the plan.
 *
 * Deliberately NOT persisted — same rule as the other Talent Mobility plan
 * drafts. LayoutV2 calls reset() once navigation leaves the PAIC flow (see
 * isPaicRedesignFlowLocation).
 */

// Client-only bookkeeping on redeployment entries that has no meaning to the
// backend (see useRedeploymentPlanStore's isActiveAvailabilityEntry).
const toPlanEmployeeTarget = ({ markedAvailable, ...entry }) => entry;

// A drafted role in the shape the backend receives. jd_sb_unique_id stays
// client-side: the backend takes each role's JD (and change_type) from the
// version itself. target_position is normalized so null/undefined compare equal.
const toPlanRole = ({ jd_sb_unique_id, ...role }) => ({
  ...role,
  target_position: role.target_position ?? null,
  available_employee_targets: role.available_employee_targets.map(toPlanEmployeeTarget),
});

const toPlanRoles = (version) => Object.values(version?.roles ?? {}).map(toPlanRole);

// What a save stores: only the roles employees were shortlisted for. The
// other drafted roles still take part in change tracking (un-shortlisting
// everyone from a saved role is a change), they just aren't persisted.
const toSavedPlanRoles = (version) =>
  toPlanRoles(version).filter((role) => role.available_employee_targets.length > 0);

// Order-insensitive serialization for change tracking: roles, entries and
// object keys can arrive in any order (spread merges, backend responses), and
// none of that ordering is a user change.
const sortKeysDeep = (value) => {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, sortKeysDeep(value[key])])
    );
  }
  return value;
};
const byKey = (key) => (a, b) => String(a[key]).localeCompare(String(b[key]));
const toComparablePlanRoles = (version) =>
  JSON.stringify(
    sortKeysDeep(
      toPlanRoles(version)
        .map((role) => ({
          ...role,
          available_employee_targets: [...role.available_employee_targets].sort(byKey('employee_unique_id')),
        }))
        .sort(byKey('role_key'))
    )
  );

const isVersionChanged = (state, versionId) =>
  toComparablePlanRoles(state.versions[versionId]) !== toComparablePlanRoles(state.pristineVersions[versionId]);

// Versions a save sends: changed ones, plus every one already in the plan.
const getPlanVersionIds = (state) =>
  Object.keys(state.versions).filter(
    (versionId) => state.savedVersionIds.includes(versionId) || isVersionChanged(state, versionId)
  );

/**
 * The draft's version other than `versionId`, if any — a plan holds one
 * version, so opening `versionId` means starting a new plan. Tells the
 * caller whether to ask first:
 *   hasUnsavedChanges — that version has changes not saved yet
 *   isInSavedPlan     — it belongs to the saved plan being edited (planId)
 * A version that was only browsed (neither) is dropped without asking.
 * Returns { versionId, hasUnsavedChanges, isInSavedPlan } or null.
 */
export const getOtherDraftVersion = (state, versionId) => {
  const otherVersionIds = Object.keys(state.versions).filter((id) => id !== versionId);
  if (!otherVersionIds.length) return null;
  const isInSavedPlan = (id) => Boolean(state.planId) && state.savedVersionIds.includes(id);
  const changedVersionId = otherVersionIds.find((id) => isVersionChanged(state, id));
  const reportedVersionId = changedVersionId ?? otherVersionIds.find(isInSavedPlan) ?? otherVersionIds[0];
  return {
    versionId: reportedVersionId,
    hasUnsavedChanges: Boolean(changedVersionId),
    isInSavedPlan: isInSavedPlan(reportedVersionId),
  };
};

const usePaicRedesignPlanStore = create(
  devtools(
    (set, get) => ({
      versions: {},
      pristineVersions: {},
      savedVersionIds: [],
      // True while the draft is a saved plan opened through View Planning's
      // Edit (loadPlan) — not a plan first saved on this page.
      isEditingSavedPlan: false,

      // plan_id / plan_name of the saved plan this draft is attached to —
      // null/"" until the first save; see usePaicRedesignPlanActions.
      planId: null,
      planName: '',

      // Idempotency key for creating this draft's plan: kept across failed
      // or timed-out create attempts so the backend can return the plan a
      // "failed" attempt actually created instead of a duplicate. Cleared
      // once the plan exists (setPlan / loadPlan) or the draft is reset.
      createRequestId: null,
      ensureCreateRequestId: () => {
        const existing = get().createRequestId;
        if (existing) return existing;
        const createRequestId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `paic-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        set({ createRequestId });
        return createRequestId;
      },

      // { versionId, roleKey, jdId } of the role currently loaded into
      // useRedeploymentPlanStore by PaicReductionDetailV2, or null.
      linkedRole: null,

      setPlan: (planId, planName) => set({ planId, planName, createRequestId: null }),

      // Adds a version with its baseline roles (buildPaicVersionBaseline).
      // For a version already in the draft (opened before, or loaded from a
      // saved plan by loadPlan, which holds only its shortlisted roles) it
      // only adds the baseline roles it's missing — to the draft and its
      // change-tracking reference alike, so they don't count as a change —
      // and never overwrites a drafted role, so edits survive a re-fetch.
      ensureVersion: (versionId, baselineRoles) => {
        if (!versionId) return;
        const baseline = Object.fromEntries(
          (baselineRoles || []).map((role) => [role.role_key, { ...role, available_employee_targets: [] }])
        );
        const state = get();
        const existing = state.versions[versionId];
        if (existing && Object.keys(baseline).every((roleKey) => existing.roles[roleKey])) return;
        const pristine = state.pristineVersions[versionId];
        set({
          versions: { ...state.versions, [versionId]: { roles: { ...baseline, ...existing?.roles } } },
          pristineVersions: {
            ...state.pristineVersions,
            [versionId]: { roles: { ...baseline, ...pristine?.roles } },
          },
        });
      },

      // View Planning's Edit action: replaces the draft with a saved plan
      // (GET /paic-redesign-plan/details organizations) as its saved state —
      // every version is in the plan and unchanged, so the Save buttons stay
      // disabled until the user changes something, then update this plan.
      loadPlan: ({ planId, planName, organizations }) => {
        const versions = Object.fromEntries(
          (organizations || []).map(({ sb_org_redesign_unique_id: versionId, roles }) => [
            versionId,
            {
              roles: Object.fromEntries(
                (roles || []).map((role) => [
                  role.role_key,
                  { ...role, available_employee_targets: role.available_employee_targets || [] },
                ])
              ),
            },
          ])
        );
        set({
          versions,
          pristineVersions: versions,
          savedVersionIds: Object.keys(versions),
          planId,
          planName,
          createRequestId: null,
          linkedRole: null,
          isEditingSavedPlan: true,
        });
      },

      // Partial update of one drafted role; no-op for a version/role that
      // isn't in the draft.
      updateRole: (versionId, roleKey, changes) =>
        set((state) => {
          const role = state.versions[versionId]?.roles[roleKey];
          if (!role) return state;
          const version = state.versions[versionId];
          return {
            versions: {
              ...state.versions,
              [versionId]: { ...version, roles: { ...version.roles, [roleKey]: { ...role, ...changes } } },
            },
          };
        }),

      // Roles Added (INCREASED) roles: a candidate shortlisted to FILL the
      // role — stored like any other pick, targeted at the role itself (see
      // PaicRoleCandidatesV2 and Candidate Profile's ?flow=paic). Re-adding
      // an existing candidate only refreshes their notify choices.
      addRoleCandidate: (versionId, roleKey, employeeId, jdId, notifications) => {
        const entries = get().versions[versionId]?.roles[roleKey]?.available_employee_targets || [];
        const entry = {
          employee_unique_id: employeeId,
          targeted_jd_sb_unique_ids: [jdId],
          notifications: { ...DEFAULT_NOTIFICATIONS, ...notifications },
          is_retained: false,
          shortlist_reason: null,
        };
        const exists = entries.some((existing) => existing.employee_unique_id === employeeId);
        get().updateRole(versionId, roleKey, {
          available_employee_targets: exists
            ? entries.map((existing) => (existing.employee_unique_id === employeeId ? entry : existing))
            : [...entries, entry],
        });
      },

      removeRoleCandidate: (versionId, roleKey, employeeId) => {
        const entries = get().versions[versionId]?.roles[roleKey]?.available_employee_targets || [];
        get().updateRole(versionId, roleKey, {
          available_employee_targets: entries.filter((entry) => entry.employee_unique_id !== employeeId),
        });
      },

      linkRole: (linkedRole) => set({ linkedRole }),
      unlinkRole: () => set({ linkedRole: null }),

      // The save payload plus the draft it was built from — markSaved needs
      // that exact draft, not whatever the user edits while the request is
      // in flight (those edits must stay unsaved).
      preparePlanSave: (planName) => {
        const state = get();
        const versionIds = getPlanVersionIds(state);
        return {
          savedDraft: { versions: state.versions, versionIds },
          payload: {
            plan_name: planName,
            organizations: versionIds.map((versionId) => ({
              sb_org_redesign_unique_id: versionId,
              roles: toSavedPlanRoles(state.versions[versionId]),
            })),
          },
        };
      },

      // After a successful save: the saved versions become the new reference
      // for change tracking and part of the plan from now on.
      markSaved: ({ versions, versionIds }) =>
        set((state) => ({
          pristineVersions: {
            ...state.pristineVersions,
            ...Object.fromEntries(versionIds.map((versionId) => [versionId, versions[versionId]])),
          },
          savedVersionIds: [...new Set([...state.savedVersionIds, ...versionIds])],
        })),

      reset: () =>
        set({
          versions: {},
          pristineVersions: {},
          savedVersionIds: [],
          planId: null,
          planName: '',
          createRequestId: null,
          linkedRole: null,
          isEditingSavedPlan: false,
        }),
    }),
    { name: 'PaicRedesignPlanStore' }
  )
);

// Copies the linked role's Reduction Detail edits back into the draft.
// Every PAIC entry into Reduction Detail records a target for the role
// (?target=, or the seeded draft value), so a store with no target for it
// has been reset — LayoutV2 leaving the redeployment flow, or a Save & Close
// there. That is not the user removing employees, so the link is dropped
// instead of mirroring empty data over the draft.
useRedeploymentPlanStore.subscribe((redeploymentState) => {
  const { linkedRole, updateRole, unlinkRole } = usePaicRedesignPlanStore.getState();
  if (!linkedRole) return;
  const { versionId, roleKey, jdId } = linkedRole;
  if (!(jdId in redeploymentState.targetPositions)) {
    unlinkRole();
    return;
  }
  // The payload getter applies the redeployment store's own "still
  // available" filter, so only entries it would itself save are copied.
  const { available_employee_targets: employeeTargets } = redeploymentState.getRedeploymentPlanPayload('');
  updateRole(versionId, roleKey, {
    target_position: redeploymentState.targetPositions[jdId],
    available_employee_targets: employeeTargets[jdId] ?? [],
  });
});

// Whether any drafted version differs from its baseline / last save — what
// enables the Save Plan / Save & Close Plan buttons.
export const useHasPaicRedesignPlanChanges = () =>
  usePaicRedesignPlanStore((state) =>
    Object.keys(state.versions).some((versionId) => isVersionChanged(state, versionId))
  );

// The version's drafted roles keyed by role_key, or undefined when the
// version isn't in the draft yet (stable reference either way).
export const usePaicVersionDraftRoles = (versionId) =>
  usePaicRedesignPlanStore((state) => state.versions[versionId]?.roles);

/**
 * Whether a router location is inside the PAIC Org Redesign flow: its
 * versions list, version pages and reduction detail, plus a Candidate Profile
 * opened from that reduction detail (?paic_version_id=) or from the Roles
 * Added candidates page (?flow=paic). LayoutV2 resets the draft when
 * navigation leaves it.
 */
export const isPaicRedesignFlowLocation = ({ pathname, search }) => {
  if (pathname.startsWith('/talent-mobility/paic-org-redesign')) return true;
  if (!pathname.startsWith('/talent-mobility/candidate/')) return false;
  const params = new URLSearchParams(search);
  return params.has('paic_version_id') || params.get('flow') === 'paic';
};

// Whether this PAIC draft role has the candidate shortlisted (Roles Added).
export const useIsPaicRoleCandidateShortlisted = (versionId, roleKey, employeeId) =>
  usePaicRedesignPlanStore((state) =>
    Boolean(
      state.versions[versionId]?.roles[roleKey]?.available_employee_targets?.some(
        (entry) => entry.employee_unique_id === employeeId
      )
    )
  );

export default usePaicRedesignPlanStore;
